/**
 * O que faz: Navegação do CRM em painel suave, expandido ou recolhido.
 * Onde usa: MainLayout, no desktop e no menu lateral mobile.
 * Conexões: Rotas, clínica ativa, identidade, permissão administrativa e logout.
 * Tabelas: Nenhuma consulta direta; mantém os hooks existentes.
 * Data: 08/09/2026 (contraste da barra e do item ativo em 19/10/2026).
 */
import {
  LayoutDashboard, Users, MessageSquare, CalendarDays, Settings, Shield,
  Columns, Menu, Sparkles, ChevronRight, ChevronLeft, Repeat2,
  CalendarClock, CalendarCheck2, Receipt, X,
} from 'lucide-react';
import { useEffect, useState, type ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Portal as TooltipPortal } from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';
import { useClinica } from '@/contexts/ClinicaContext';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { LogoutButton } from '@/components/auth/LogoutButton';
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { preCarregarPipeline } from '@/lib/rotas/rotaPipeline';
import { SinoAvisosAgentes } from '@/features/agentes/components/SinoAvisosAgentes';
import { AssinaturaGens, SeloGens } from '@/components/brand/MarcaGens';

type NavItem = { id: string; label: string; icon: typeof LayoutDashboard; path: string };

// Os grupos organizam acessos existentes, sem inventar conversas ou contadores da referência.
const gruposNavegacao: { label: string; items: NavItem[] }[] = [
  { label: 'Menu', items: [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
    // Chat logo após o Dashboard (07/10/2026): é a tela mais usada no dia a dia da clínica.
    { id: 'chat', label: 'Chat', icon: MessageSquare, path: '/chat' },
    { id: 'pipeline', label: 'Funil', icon: Columns, path: '/pipeline' },
    { id: 'vendas', label: 'Vendas', icon: Receipt, path: '/vendas' },
    { id: 'retornos', label: 'Retornos', icon: CalendarCheck2, path: '/retornos' },
    { id: 'contatos', label: 'Contatos', icon: Users, path: '/contatos' },
    { id: 'agenda', label: 'Agenda', icon: CalendarDays, path: '/agenda' },
  ] },
  { label: 'Automação', items: [
    { id: 'follow-up', label: 'Follow-up', icon: Repeat2, path: '/follow-up' },
    { id: 'mensagens-agendadas', label: 'Mensagens Agendadas', icon: CalendarClock, path: '/mensagens-agendadas' },
    { id: 'ia', label: 'Inteligência Artificial', icon: Sparkles, path: '/ia' },
  ] },
];

const chavePreferencia = 'gens:sidebar-recolhida';
const focoVisivel = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar';

// O portal impede que a rolagem corte os nomes na versão recolhida, inclusive no foco por teclado.
const DicaNavegacao = ({ label, ativa, children }: { label: string; ativa: boolean; children: ReactElement }) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    {ativa && <TooltipPortal>
      <TooltipContent side="right" sideOffset={14} className="rounded-xl border-0 bg-brand-strong px-4 py-2.5 font-medium text-primary-foreground shadow-lg data-[state=closed]:hidden motion-reduce:!animate-none">{label}</TooltipContent>
    </TooltipPortal>}
  </Tooltip>
);

type ConteudoSidebarProps = { recolhida?: boolean; mobile?: boolean; aoAlternar?: () => void; aoNavegar?: () => void };

// A identidade estável do componente evita remontar links e perder foco ao recolher.
const ConteudoSidebar = ({ recolhida = false, mobile = false, aoAlternar, aoNavegar }: ConteudoSidebarProps) => {
  const { pathname } = useLocation();
  const { clinicaAtiva } = useClinica();
  const { getDisplayName, getInitials } = useAuthUser();
  const { isAdmin } = useAdminCheck();
  const nomeUsuario = getDisplayName() || clinicaAtiva?.nome || 'Usuário';

  const renderizarLink = (item: NavItem) => {
    const Icon = item.icon;
    // Subpáginas mantêm o contexto do menu sem confundir prefixos semelhantes.
    const ativo = pathname === item.path || pathname.startsWith(`${item.path}/`);
    return (
      <li key={item.id}>
        <DicaNavegacao label={item.label} ativa={recolhida}>
          <Link to={item.path} onClick={aoNavegar}
            onMouseEnter={item.id === 'pipeline' ? () => void preCarregarPipeline() : undefined}
            onFocus={item.id === 'pipeline' ? () => void preCarregarPipeline() : undefined}
            aria-current={ativo ? 'page' : undefined}
            className={cn('group flex h-11 items-center gap-1 rounded-2xl text-[13px] transition-colors duration-150 motion-reduce:!transition-none', focoVisivel,
              // Ativo em lima com texto tinta (9,7:1) e peso maior: a página atual se destaca do musgo.
              ativo ? 'bg-sidebar-accent font-semibold text-sidebar-accent-foreground' : 'font-medium text-sidebar-foreground hover:bg-card/80 hover:text-foreground')}
          >
            {/* A coluna fixa mantém os ícones nas mesmas coordenadas ao recolher ou expandir. */}
            <span className="flex h-11 w-11 shrink-0 items-center justify-center">
              <Icon aria-hidden size={19} strokeWidth={ativo ? 2.2 : 1.7} className={cn('shrink-0', ativo ? 'text-sidebar-accent-foreground' : 'text-muted-foreground group-hover:text-sidebar-accent-foreground')} />
            </span>
            <span className={cn(recolhida ? 'sr-only' : 'min-w-0 flex-1 truncate pr-2')}>{item.label}</span>
            {ativo && !recolhida && <span aria-hidden className="mr-3 h-1.5 w-1.5 shrink-0 rounded-full bg-sidebar-accent-foreground" />}
          </Link>
        </DicaNavegacao>
      </li>
    );
  };

  return (
    // Fundo musgo com borda sólida separa a barra das páginas brancas e papel. O apoio fixo
    // (--muted-foreground) mantém rótulos e ícones em 5,1:1 em qualquer tela, migrada ou não.
    // O degradê antigo saiu: ele mudava de tom conforme a página (lima ou esmeralda).
    <div className="relative flex h-full min-h-0 flex-col rounded-[26px] border border-sidebar-border bg-sidebar shadow-[0_8px_32px_-20px_hsl(var(--foreground)/0.2)] [--muted-foreground:var(--sidebar-muted-foreground)]">
      {/* Logo no topo e controle independente; altura igual preserva os ícones durante o slide. */}
      <header className={cn('relative h-[124px] shrink-0 px-5 pb-5 pt-5', recolhida && 'px-3')}>
        <div className={cn('absolute right-3 top-5 flex items-center', recolhida && 'inset-x-0 top-[68px] justify-center')}>
          {mobile ? (
            <SheetClose asChild>
              <button type="button" aria-label="Fechar menu de navegação" className={cn('flex h-11 w-11 items-center justify-center rounded-full bg-card text-sidebar-foreground', focoVisivel)}><X aria-hidden size={18} /></button>
            </SheetClose>
          ) : (
            // Hover em lima névoa (brand-subtle): o lima forte fica reservado ao item ativo.
            <button type="button" onClick={aoAlternar} aria-label={recolhida ? 'Expandir menu de navegação' : 'Recolher menu de navegação'} aria-expanded={!recolhida} aria-controls="navegacao-desktop"
              className={cn('flex h-9 w-9 items-center justify-center rounded-full border border-sidebar-border bg-card text-sidebar-foreground transition-colors hover:bg-brand-subtle motion-reduce:!transition-none', focoVisivel)}>
              {recolhida ? <ChevronRight aria-hidden size={17} /> : <ChevronLeft aria-hidden size={17} />}
            </button>
          )}
        </div>
        <Link to="/dashboard" onClick={aoNavegar} aria-label="Gens CRM — Dashboard" className={cn('flex w-fit items-center gap-3 rounded-xl', focoVisivel, recolhida && 'mx-auto justify-center')}>
          {/* Motivo: símbolo "g." vetorial (MarcaGens) substitui as imagens antigas e fica nítido em qualquer tela. */}
          {recolhida ? <SeloGens redondo className="h-10 w-10" /> : (
            <div><AssinaturaGens className="text-[22px] text-sidebar-foreground" classeSelo="h-8 w-8" /><p className="mt-2 text-[11px] text-muted-foreground">Sistema de Gestão</p></div>
          )}
        </Link>
      </header>
      {/* Trilho transparente: o trilho claro padrão do Windows virava uma faixa sobre o musgo. */}
      <nav id={mobile ? 'navegacao-mobile' : 'navegacao-desktop'} aria-label="Navegação principal" className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-2 [scrollbar-color:hsl(var(--sidebar-foreground)/0.3)_transparent] [scrollbar-width:thin]">
        {gruposNavegacao.map((grupo, indice) => (
          <section key={grupo.label} aria-label={grupo.label} className={cn(indice > 0 && 'mt-4 border-t border-sidebar-border/60 pt-4')}>
            <h2 className={cn('mb-2 h-4 px-3 text-[10px] font-semibold uppercase leading-4 tracking-[0.1em] text-muted-foreground', recolhida && 'px-0 text-center text-[8px] tracking-normal')}>{grupo.label}</h2>
            {/* Sem bloco branco atrás da Automação: sobre o musgo ele apagaria o hover branco dos itens. */}
            <ul className="space-y-1">{grupo.items.map(renderizarLink)}</ul>
          </section>
        ))}
        <ul className="mt-3 space-y-1 border-t border-sidebar-border/60 pt-3">
          {renderizarLink({ id: 'configuracoes', label: 'Configurações', icon: Settings, path: '/configuracoes' })}
          {isAdmin && renderizarLink({ id: 'admin', label: 'Admin', icon: Shield, path: '/admin' })}
        </ul>
      </nav>
      <footer className="relative shrink-0 p-2">
        <div className="flex items-center gap-3 border-t border-sidebar-border/60 pb-2 pt-4" title={recolhida ? `${nomeUsuario} — ${isAdmin ? 'Administrador' : 'Usuário'}` : undefined}>
          {/* Avatar em lima névoa (padrão de avatar do design system): o lima forte fica só no item ativo. */}
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-card bg-brand-subtle text-sm font-semibold text-sidebar-accent-foreground">{getInitials()}</div>
          <div className={cn('min-w-0', recolhida && 'sr-only')}><p className="truncate text-[13px] font-semibold text-foreground">{nomeUsuario}</p><p className="mt-0.5 text-[11px] text-muted-foreground">{isAdmin ? 'Administrador' : 'Usuário'}</p></div>
        </div>
        {/* Avisos dos agentes: só aparece quando a clínica tem agentes ligados; usa contagens baratas, sem Realtime. */}
        <SinoAvisosAgentes clinicaId={clinicaAtiva?.id ?? null} modoAdmin={isAdmin} recolhida={recolhida} />
        {/* Oculta apenas o rótulo visual; mantém nome acessível e fluxo de logout existentes. */}
        <LogoutButton variant="ghost" size="sm" className={cn('min-h-9 w-full justify-start rounded-xl pl-[14px] text-muted-foreground hover:bg-card hover:text-foreground', recolhida && '[&>span]:sr-only')} />
      </footer>
    </div>
  );
};

export const Sidebar = () => {
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Armazenamento bloqueado não impede navegação; o fallback é o painel expandido.
  const [recolhida, setRecolhida] = useState(() => {
    try { return localStorage.getItem(chavePreferencia) === 'true'; } catch { return false; }
  });
  useEffect(() => { setMobileOpen(false); }, [pathname]);
  const alternarSidebar = () => {
    const proximoEstado = !recolhida;
    setRecolhida(proximoEstado);
    // Preferência local não altera a conta nem exige acesso remoto.
    try { localStorage.setItem(chavePreferencia, String(proximoEstado)); } catch { /* Continua funcional sem persistência. */ }
  };
  return (
    <TooltipProvider delayDuration={150}>
      <div className="fixed inset-x-0 top-0 z-50 flex h-14 items-center gap-3 border-b border-sidebar-border bg-sidebar px-3 md:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild><button type="button" aria-label="Abrir menu de navegação" aria-expanded={mobileOpen} className={cn('flex h-11 w-11 items-center justify-center rounded-xl hover:bg-card', focoVisivel)}><Menu aria-hidden size={23} /></button></SheetTrigger>
          <SheetContent side="left" aria-describedby={undefined} overlayClassName="z-[60] motion-reduce:!animate-none" className="z-[60] w-[300px] max-w-[calc(100vw-16px)] border-0 bg-transparent p-2 shadow-none [&>button]:hidden duration-200 data-[state=open]:duration-200 data-[state=closed]:duration-150 motion-reduce:!animate-none motion-reduce:!transition-none">
            <SheetTitle className="sr-only">Menu de navegação</SheetTitle>
            <ConteudoSidebar mobile aoNavegar={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <AssinaturaGens className="text-lg text-sidebar-foreground" classeSelo="h-7 w-7" />
      </div>
      {/* A largura desliza por 220 ms; a coluna fixa dos ícones impede deslocamento durante o efeito. */}
      <aside aria-label="Menu lateral" className={cn('hidden shrink-0 flex-col p-3 pr-0 transition-[width] [transition-duration:220ms] ease-out motion-reduce:!transition-none md:flex', recolhida ? 'w-[92px]' : 'w-[280px]')}><ConteudoSidebar recolhida={recolhida} aoAlternar={alternarSidebar} /></aside>
    </TooltipProvider>
  );
};
